import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loan } from '@matech/thebigpos-sdk'
import { BreadCrumbItemProps } from '@/components/breadcrumbs/BreadcrumbItem'
// eslint-disable-next-line import/no-cycle
import { useAlert } from '../hooks'
import queryKeys from '../services/queryKeys'
import { TheBigPOSApi } from '../lib/TheBigPOSClient'
import { LoanSignalRContextProvider } from './LoanSignalRContext'
import { LoanData } from '../types'
import useUserAgent from '../hooks/useUserAgent'
// eslint-disable-next-line import/no-cycle
import { LoanLockedContextProvider } from './LoanLockedContext'
import { history } from '../components/CustomBrowserRouter'
import { navigationLinking } from '../services/navigation'

export type LoanContextValue = {
	loanId: string
	loan?: Loan
	loanData?: LoanData
	isLoading: boolean
	isRefetching: boolean
	refetch?: () => Promise<void>
	postLoanData?: LoanData | null
	setActiveLoanId: (newLoanId: string) => void
	setPostLoanData: (newTempLoanData: LoanData | null) => void
	activeLoanNavigationCrumb: BreadCrumbItemProps
}

export type LoanContextProviderProps = {
	children: React.ReactNode
}

const defaultContextValues: LoanContextValue = {
	loanId: '',
	isLoading: false,
	isRefetching: false,
	setActiveLoanId: () => {},
	setPostLoanData: () => {},
	activeLoanNavigationCrumb: {
		label: '',
		value: '',
		onClick() {
			history.push(`/${navigationLinking.Portal}`)
		},
	},
}

export const LoanContext = React.createContext(defaultContextValues)

export const LoanContextProvider = ({
	children,
}: LoanContextProviderProps) => {
	const { alert } = useAlert()
	const { mobileOs } = useUserAgent()

	const [loanId, setLoanId] = React.useState<string>('')
	const [postLoanData, setPostLoanData] =
		React.useState<LoanData | null>()

	const setActiveLoanId = useCallback((newLoanId: string) => {
		setLoanId(newLoanId)
	}, [])

	const handleSetPostLoanData = useCallback(
		(newPostLoanData: LoanData | null) => {
			setPostLoanData(newPostLoanData)
		},
		[]
	)

	const {
		isLoading,
		isRefetching,
		data,
		refetch: refetchLoan,
		error,
	} = useQuery({
		queryKey: [queryKeys.loan, loanId],
		queryFn: () => TheBigPOSApi.getLoan(loanId),
		enabled: !!loanId,
	})

	const {
		isLoading: isLoanDataLoading,
		isRefetching: isLoanDataRefetching,
		data: loanData,
		refetch: refetchLoanData,
		error: loanDataError,
	} = useQuery({
		queryKey: [queryKeys.loanData, loanId],
		queryFn: () => TheBigPOSApi.getLoanData(loanId),
		enabled: !!loanId,
		refetchOnWindowFocus: mobileOs !== 'android',
	})

	const refetch = useCallback(async () => {
		await refetchLoan()
		await refetchLoanData()
	}, [refetchLoan, refetchLoanData])

	const value: LoanContextValue = useMemo(
		() => ({
			loanId,
			loan: data?.data,
			loanData: loanData?.data,
			isLoading: isLoading || isLoanDataLoading,
			isRefetching: isRefetching || isLoanDataRefetching,
			refetch,
			postLoanData,
			setActiveLoanId,
			setPostLoanData: handleSetPostLoanData,
			activeLoanNavigationCrumb: {
				label: data?.data.loanNumber
					? `#${data.data.loanNumber}`
					: '',
				value: data?.data.id ?? '',
				onClick() {
					// TODO: have the loan app switcher
					history.push(`/${navigationLinking.Portal}`)
				},
			},
		}),
		[
			data?.data,
			isLoading,
			isLoanDataLoading,
			isLoanDataRefetching,
			isRefetching,
			loanData?.data,
			refetch,
			loanId,
			postLoanData,
			setActiveLoanId,
			handleSetPostLoanData,
		]
	)

	useEffect(() => {
		if ((error && data?.status !== 404) || loanDataError) {
			const errorMessage =
				error?.message ??
				loanDataError?.message ??
				'There was a problem fetching the loan'
			alert(errorMessage, {
				severity: 'error',
			})
		}
	}, [alert, data?.status, error, loanDataError])

	return (
		<LoanSignalRContextProvider>
			<LoanContext.Provider value={value}>
				<LoanLockedContextProvider>
					{children}
				</LoanLockedContextProvider>
			</LoanContext.Provider>
		</LoanSignalRContextProvider>
	)
}

export const useLoanContext = () => useContext(LoanContext)
